import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import type { AsyncState } from '../common';
import { useCurrentUser } from '../current-user';
import type { PendingEntry } from '../worklog/models';
import { loadMonth } from './api';
import { applyEntryToCell, type CallerStamp } from './applyEntryToCell';
import { useCurrentMonth } from './CurrentMonthProvider';
import type { DayCell } from './models';

const toError = (e: unknown): Error => (e instanceof Error ? e : new Error(String(e)));

type MonthCellsContextValue = {
    state: AsyncState<DayCell[]>;
    apply: (entries: PendingEntry[]) => void;
    refresh: () => void;
};

const MonthCellsContext = createContext<MonthCellsContextValue | undefined>(undefined);

export const MonthCellsProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const { current } = useCurrentMonth();
    const { user, actor, daysOff, daysOffVersion } = useCurrentUser();
    const [state, setState] = useState<AsyncState<DayCell[]>>({ status: 'loading' });
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        if (!user) {
            setState({ status: 'loading' });
            return;
        }
        let cancelled = false;
        setState({ status: 'loading' });
        loadMonth(user.id, current.year, current.month, daysOff).then(
            (data) => {
                if (!cancelled) {
                    setState({ status: 'success', data });
                }
            },
            (e: unknown) => {
                if (!cancelled) {
                    setState({ status: 'error', error: toError(e) });
                }
            },
        );
        return () => {
            cancelled = true;
        };
    }, [user, current.year, current.month, daysOffVersion, reloadTick]);

    const apply = useCallback(
        (entries: PendingEntry[]) => {
            const caller: CallerStamp = { developer: user?.name ?? '', authorEmail: actor?.email ?? '' };
            setState((prev) => {
                if (prev.status !== 'success') {
                    return prev;
                }
                const byDate = new Map<string, DayCell>();
                for (const cell of prev.data) {
                    byDate.set(cell.date, cell);
                }
                for (const entry of entries) {
                    const cell = byDate.get(entry.date);
                    if (!cell) {
                        continue;
                    }
                    byDate.set(entry.date, applyEntryToCell(cell, entry, caller));
                }
                return { status: 'success', data: prev.data.map((c) => byDate.get(c.date) ?? c) };
            });
        },
        [user, actor],
    );

    const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

    const value = useMemo<MonthCellsContextValue>(() => ({ state, apply, refresh }), [state, apply, refresh]);

    return <MonthCellsContext.Provider value={value}>{children}</MonthCellsContext.Provider>;
};

export const useMonthCells = (): MonthCellsContextValue => {
    const ctx = useContext(MonthCellsContext);
    if (!ctx) {
        throw new Error('useMonthCells must be used within a MonthCellsProvider');
    }
    return ctx;
};
